import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMe } from '../../api/generated/identity/identity';
import {
  useCreateBatchReceiptDownloadURL,
  useGetMyEarnings,
} from '../../api/generated/payroll/payroll';
import type { PayrollItemView, PayrollReceipt } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { formatInPersonTimezone } from '../../i18n/datetime';
import { formatMoney } from '../billing/money';
import { currentPeriod, isPeriod } from './period';
import styles from './payroll.module.css';

/**
 * Ganhos do professor numa competencia.
 *
 * Duas decisoes governam esta tela:
 *
 * - **Nenhum nome de aluno, nem mascarado.** O item de folha referencia a
 *   ocorrencia, e nao o aluno: a lista mostra data, horario e tipo da aula. Nao
 *   e uma escolha de layout — dupla e individual geram **um** item so, e um
 *   nome ali seria uma informacao que o contrato nao carrega e que a folha nao
 *   precisa.
 * - **Apurado e ajustado lado a lado.** Mostrar so o ajustado e, do ponto de
 *   vista de quem confere meses depois, o mesmo que sobrescrever: o professor
 *   perde a unica evidencia de que o valor mudou e por que.
 */
export function TeacherEarnings() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  const [period, setPeriod] = useState(currentPeriod);
  const valid = isPeriod(period);

  const earnings = useGetMyEarnings({ period }, { query: { enabled: !!me && valid } });

  if (!me) return null;

  const summary = earnings.data;

  return (
    <div className={styles.page}>
      <h1>{t('payroll.earnings.title')}</h1>

      <Field
        label={t('payroll.period')}
        placeholder={t('payroll.periodPlaceholder')}
        value={period}
        onChange={(event) => setPeriod(event.target.value)}
        error={valid ? undefined : t('payroll.periodInvalid')}
      />

      {earnings.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {summary ? (
        <>
          <section className={styles.summary} aria-label={t('payroll.earnings.totals')}>
            <p className={styles.figure}>
              <span>{t('payroll.earnings.payableTotal')}</span>
              <strong className={styles.figureValue}>
                {formatMoney(summary.payableTotal, me.locale)}
              </strong>
            </p>
            <p className={styles.figure}>
              <span>{t('payroll.earnings.heldTotal')}</span>
              <strong className={styles.figureValue}>
                {formatMoney(summary.heldTotal, me.locale)}
              </strong>
              {/*
                Retido nao e "nao pago": e a aula perdida cujo desfecho de
                reposicao ainda decide quem recebe. Sem esta frase, o total menor
                que o esperado parece erro de calculo.
              */}
              <span className={styles.meta}>{t('payroll.earnings.heldExplained')}</span>
            </p>
          </section>

          <section aria-label={t('payroll.earnings.classes')}>
            <h2>{t('payroll.earnings.classes')}</h2>
            <p className={styles.meta}>{t('payroll.noStudentNames')}</p>

            {summary.items.length === 0 ? (
              <p className={styles.meta}>{t('payroll.earnings.empty')}</p>
            ) : (
              <ul className={styles.list}>
                {summary.items.map((item) => (
                  <PayrollItemCard key={item.id} item={item} locale={me.locale} />
                ))}
              </ul>
            )}
          </section>

          {/*
            O contrato passou a projetar o estado do lote e o comprovante no
            proprio demonstrativo, em vez de abrir a leitura do lote ao
            professor: os dois recortes ja sao por professor e competencia,
            entao nao ha vazamento — e abrir o lote entregaria quem aprovou o
            pagamento dele.
          */}
          <BatchStanding batchState={summary.batchState} receipt={summary.receipt} />
        </>
      ) : null}
    </div>
  );
}

/**
 * Situacao do lote da competencia, e o comprovante quando ele existe.
 *
 * **O comprovante so aparece liquidado.** `EarningsSummary.receipt` e nao nulo
 * somente depois de confirmado, e entre o anexo e a confirmacao pode haver
 * comprovante sem bytes no bucket — oferecer o link ali daria ao professor um
 * download que falha, que e pior do que nao oferecer nada.
 *
 * Sem lote aberto, `batchState` e nulo junto com `batchId`, e a tela diz que a
 * competencia ainda esta em apuracao em vez de mostrar um estado vazio.
 */
function BatchStanding({
  batchState,
  receipt,
}: {
  readonly batchState: string | null;
  readonly receipt: PayrollReceipt | null;
}) {
  const { t } = useTranslation();
  const download = useCreateBatchReceiptDownloadURL();

  if (batchState === null) {
    return <p className={styles.meta}>{t('payroll.earnings.batchNone')}</p>;
  }

  return (
    <section aria-label={t('payroll.earnings.batchStanding')}>
      <h2>{t('payroll.earnings.batchStanding')}</h2>
      <p>{t(`payroll.batchState.${batchState}`)}</p>

      {receipt === null ? (
        <p className={styles.meta}>{t('payroll.earnings.receiptPending')}</p>
      ) : (
        <div className={styles.actions}>
          <Button
            variant="secondary"
            loading={download.isPending}
            onClick={() =>
              download.mutate(
                { batchId: receipt.batchId, receiptId: receipt.receiptId },
                {
                  onSuccess: (signed) => {
                    // Abre na hora e descarta: URL assinada em estado ou cache e
                    // URL assinada vazando por historico de navegador.
                    window.open(signed.url, '_blank', 'noopener,noreferrer');
                  },
                },
              )
            }
          >
            {t('payroll.earnings.receiptDownload')}
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Uma aula elegivel.
 *
 * O rotulo acessivel do item e data, horario e tipo — que e tudo o que
 * identifica a aula sem identificar ninguem.
 */
export function PayrollItemCard({
  item,
  locale,
  children,
}: {
  readonly item: PayrollItemView;
  readonly locale: string;
  /** Acoes sobre o item, quando quem olha pode agir — o ajuste do Super Admin. */
  readonly children?: ReactNode;
}) {
  const { t } = useTranslation();
  // Fuso ancora da ocorrencia, e nao o do leitor: a aula aconteceu numa hora
  // local, e e essa hora que o professor reconhece na propria agenda.
  const when = formatInPersonTimezone(item.occurrenceStartsAtUtc, item.anchorTimezone, locale);
  const adjusted = item.adjustedAmount.amountCents !== item.computedAmount.amountCents;

  return (
    <li className={styles.card} aria-label={`${when} · ${t(`payroll.kind.${item.occurrenceKind}`)}`}>
      <p>
        <strong>{when}</strong> · <span>{t(`payroll.kind.${item.occurrenceKind}`)}</span> ·{' '}
        <span>{t(`payroll.itemState.${item.state}`)}</span>
      </p>

      <dl className={styles.amounts}>
        <div className={styles.amount}>
          <dt>{t('payroll.computedAmount')}</dt>
          <dd>{formatMoney(item.computedAmount, locale)}</dd>
        </div>
        <div className={styles.amount}>
          <dt>{t('payroll.adjustedAmount')}</dt>
          <dd>{formatMoney(item.adjustedAmount, locale)}</dd>
        </div>
      </dl>

      {/*
        O motivo do ajuste fica junto do valor, e nao numa tela de auditoria: o
        professor precisa saber por que recebeu diferente sem ter de perguntar.
      */}
      {adjusted
        ? item.adjustments.map((adjustment) => (
            <p key={`${adjustment.adjustedAt}-${adjustment.reason}`} className={styles.meta}>
              {t('payroll.adjustmentLine', {
                amount: formatMoney(adjustment.amount, locale),
                reason: adjustment.reason,
              })}
            </p>
          ))
        : null}

      {children}
    </li>
  );
}
